import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { avatarUrl } from '../utils/fileUrl';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users,
  UsersThree,
  UserPlus,
  Check,
  X,
  PaperPlaneTilt,
  SpinnerGap,
  ChatCircle,
  MagnifyingGlass,
  Heart
} from '@phosphor-icons/react';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BuddiesPage = () => {
  const { lang } = useLang();
  const isDe = lang === 'de';
  const [activeTab, setActiveTab] = useState('buddies');
  const [buddies, setBuddies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBuddy, setSelectedBuddy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  // Privacy-first search for /buddies "Finden" tab — students cannot browse
  // the full roster; they must type ≥3 chars of a name. Mirrors the rules of
  // ChallengePage's friends panel.
  const [findQuery, setFindQuery] = useState('');
  const [findBusy, setFindBusy] = useState(false);

  useEffect(() => {
    const q = findQuery.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setFindBusy(false);
      return;
    }
    setFindBusy(true);
    const t = setTimeout(() => {
      axios.get(`${API}/buddies/suggestions?q=${encodeURIComponent(q)}`, { withCredentials: true })
        .then(r => setSuggestions(r.data || []))
        .catch(() => setSuggestions([]))
        .finally(() => setFindBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [findQuery]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedBuddy) {
      loadChat(selectedBuddy.connection_id);
    }
  }, [selectedBuddy]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      const [buddiesRes, requestsRes] = await Promise.all([
        axios.get(`${API}/buddies/my-buddies`, { withCredentials: true }),
        axios.get(`${API}/buddies/requests`, { withCredentials: true })
      ]);
      setBuddies(buddiesRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Error loading buddies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (connectionId) => {
    try {
      const res = await axios.get(`${API}/buddies/chat/${connectionId}`, { withCredentials: true });
      setMessages(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Chats');
    }
  };

  const sendBuddyRequest = async (targetUserId) => {
    try {
      await axios.post(`${API}/buddies/request`, {
        target_user_id: targetUserId,
        message: "¡Hola! Me gustaría practicar español contigo."
      }, { withCredentials: true });
      
      toast.success('Anfrage gesendet!');
      setSuggestions(suggestions.filter(s => s.user_id !== targetUserId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const respondToRequest = async (connectionId, accept) => {
    try {
      await axios.post(`${API}/buddies/respond/${connectionId}?accept=${accept}`, {}, { withCredentials: true });
      
      toast.success(accept ? 'Buddy hinzugefügt! +20 Punkte' : 'Anfrage abgelehnt');
      loadData();
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedBuddy) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/buddies/chat/${selectedBuddy.connection_id}`, {
        buddy_id: selectedBuddy.user_id,
        content: newMessage
      }, { withCredentials: true });
      
      setMessages([...messages, res.data]);
      setNewMessage('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      const msg = typeof detail === 'string' ? detail
        : !status ? 'Verbindungsfehler. Bitte Internetverbindung prüfen.'
        : `Fehler ${status}`;
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SpinnerGap size={40} className="animate-spin text-[#AB1C24]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="buddies-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
          <UsersThree size={28} weight="duotone" className="text-[#2F6B50]" />
          {isDe ? 'Freunde' : 'Amigos'}
        </h1>
        <p className="text-[#57534E] mt-1">{isDe ? 'Finde Lernpartner auf deinem Niveau' : 'Encuentra compañeros de aprendizaje de tu nivel'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Buddy List */}
        <div className="lg:col-span-1 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-[#F5F5F4] p-1 rounded-xl">
              <TabsTrigger value="buddies" className="flex-1 rounded-lg data-[state=active]:bg-white">
                <Users size={18} className="mr-1" />
                Buddies ({buddies.length})
              </TabsTrigger>
              <TabsTrigger value="find" className="flex-1 rounded-lg data-[state=active]:bg-white">
                <MagnifyingGlass size={18} className="mr-1" />
                Finden
              </TabsTrigger>
            </TabsList>

            <TabsContent value="buddies" className="mt-4 space-y-3">
              {requests.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-amber-800 mb-3">
                    {requests.length} neue Anfrage(n)
                  </p>
                  {requests.map((req) => (
                    <div key={req.connection_id} className="flex items-center justify-between bg-white rounded-lg p-3 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#AB1C24] flex items-center justify-center text-white font-bold">
                          {req.requester?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{req.requester?.name}</p>
                          <p className="text-xs text-[#78716C]">{req.requester?.spanish_level}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => respondToRequest(req.connection_id, true)}
                          className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200"
                          data-testid={`accept-${req.connection_id}`}
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => respondToRequest(req.connection_id, false)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {buddies.length > 0 ? (
                buddies.map((buddy) => (
                  <button
                    key={buddy.user_id}
                    onClick={() => setSelectedBuddy(buddy)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
                      selectedBuddy?.user_id === buddy.user_id 
                        ? 'border-[#AB1C24] bg-[#AB1C24]/5' 
                        : 'border-[#D6D3D1] hover:border-[#AB1C24]/50'
                    }`}
                    data-testid={`buddy-${buddy.user_id}`}
                  >
                    {buddy.avatar ? (
                      <img src={avatarUrl(buddy.avatar)} alt={buddy.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5B8C9A] to-[#6B8E23] flex items-center justify-center text-white font-bold text-lg">
                        {buddy.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <p className="font-medium text-[#292524]">{buddy.name}</p>
                      <div className="flex items-center gap-2">
                        <span className={`level-badge ${buddy.spanish_level} text-xs`}>{buddy.spanish_level}</span>
                        {buddy.last_message && (
                          <span className="text-xs text-[#78716C] truncate max-w-[100px]">
                            {buddy.last_message.content}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-[#78716C]">
                  <Users size={40} className="mx-auto mb-3 opacity-50" />
                  <p>Noch keine Buddies</p>
                  <p className="text-sm">Finde Lernpartner im "Finden"-Tab</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="find" className="mt-4 space-y-3">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
                <input
                  type="text"
                  value={findQuery}
                  onChange={e => setFindQuery(e.target.value)}
                  placeholder={isDe ? 'Vollständigen Namen tippen (mind. 3 Buchstaben)…' : 'Escribe el nombre completo (mín. 3 letras)…'}
                  className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#AB1C24]"
                  data-testid="buddies-find-search"
                />
              </div>
              {findQuery.trim().length > 0 && findQuery.trim().length < 3 ? (
                <p className="text-[11px] text-[#A8A29E] italic">
                  {isDe ? 'Mind. 3 Buchstaben — wir schützen die Privatsphäre der Mitlernenden.' : 'Mínimo 3 letras — protegemos la privacidad de los alumnos.'}
                </p>
              ) : findBusy ? (
                <p className="text-xs text-[#A8A29E] italic">{isDe ? 'Suche läuft…' : 'Buscando…'}</p>
              ) : findQuery.trim().length >= 3 && suggestions.length === 0 ? (
                <p className="text-xs text-[#A8A29E] italic">{isDe ? 'Keine Treffer.' : 'Sin coincidencias.'}</p>
              ) : suggestions.length > 0 ? (
                suggestions.map((user) => (
                  <div 
                    key={user.user_id}
                    className="flex items-center justify-between p-4 rounded-xl border border-[#D6D3D1] hover:border-[#AB1C24]/50 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      {user.avatar ? (
                        <img src={avatarUrl(user.avatar)} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#AB1C24] to-[#5B8C9A] flex items-center justify-center text-white font-bold text-lg">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#292524]">{user.name}</p>
                        <span className={`level-badge ${user.spanish_level} text-xs`}>{user.spanish_level}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => sendBuddyRequest(user.user_id)}
                      size="sm"
                      className="bg-[#AB1C24] hover:bg-[#8A151C]"
                      data-testid={`request-${user.user_id}`}
                    >
                      <UserPlus size={18} className="mr-1" />
                      {isDe ? 'Anfragen' : 'Invitar'}
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-[#78716C]">
                  <MagnifyingGlass size={40} className="mx-auto mb-3 opacity-50" />
                  <p>{isDe ? 'Tippe einen Namen' : 'Escribe un nombre'}</p>
                  <p className="text-sm">{isDe ? 'Wir zeigen niemanden ungefragt.' : 'No mostramos a nadie sin búsqueda.'}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Panel - Chat */}
        <div className="lg:col-span-2">
          {selectedBuddy ? (
            <div className="bg-white rounded-2xl border border-[#D6D3D1] h-[600px] flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-[#D6D3D1] flex items-center gap-3">
                {selectedBuddy.avatar ? (
                  <img src={avatarUrl(selectedBuddy.avatar)} alt={selectedBuddy.name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5B8C9A] to-[#6B8E23] flex items-center justify-center text-white font-bold">
                    {selectedBuddy.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-medium text-[#292524]">{selectedBuddy.name}</p>
                  <p className="text-xs text-[#78716C]">Niveau: {selectedBuddy.spanish_level}</p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.length > 0 ? (
                    messages.map((msg, idx) => (
                      <div 
                        key={msg.message_id || idx}
                        className={`flex ${msg.sender_id === selectedBuddy.user_id ? 'justify-start' : 'justify-end'}`}
                      >
                        <div className={`max-w-[70%] p-3 rounded-2xl ${
                          msg.sender_id === selectedBuddy.user_id 
                            ? 'bg-[#F5F5F4] text-[#292524]' 
                            : 'bg-[#AB1C24] text-white'
                        }`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === selectedBuddy.user_id ? 'text-[#78716C]' : 'text-white/70'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Heart size={40} className="mx-auto text-[#AB1C24] mb-3" />
                      <p className="text-[#78716C]">Starte die Konversation!</p>
                      <p className="text-sm text-[#78716C]">Übe Spanisch mit deinem Buddy</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 border-t border-[#D6D3D1]">
                <div className="flex gap-3">
                  <Input
                    placeholder="Schreibe eine Nachricht auf Spanisch..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1 rounded-xl"
                    data-testid="buddy-message-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#AB1C24] hover:bg-[#8A151C] rounded-xl"
                    data-testid="send-buddy-message-btn"
                  >
                    {sending ? <SpinnerGap size={20} className="animate-spin" /> : <PaperPlaneTilt size={20} />}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#D6D3D1] h-[600px] flex items-center justify-center">
              <div className="text-center">
                <ChatCircle size={60} className="mx-auto text-[#AB1C24]/30 mb-4" />
                <p className="text-[#78716C] font-medium">Wähle einen Buddy aus</p>
                <p className="text-sm text-[#78716C]">um zu chatten und Spanisch zu üben</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuddiesPage;
